<template>
    <div class="photo-cropper">
        <i class="photo-cropper__icon fas fa-camera" :size="iconSize" data-qe-id="photo-cropper-btn" @click="openPhotoDialog()">
        </i>
        <v-dialog
            scrollable
            v-model="uploadPhotoDialog"
            content-class="medium">
            <v-card class="photo-cropper__dialog" v-if="uploadPhotoDialog">
                <v-card-title primary-title>
                    {{cropperTitle}}
                    <i class="fas fa-times" data-qe-id="cancel_dialog_btn" @click="uploadPhotoDialog = false"></i>
                </v-card-title>
                <v-card-text class="upload-photo inner-scrollbar">
                    <croppa
                        v-model="croppa"
                        :width="530"
                        :height="400"
                        :quality="1"
                        canvas-color="transparent"
                        :placeholder="cropperPlaceholder"
                        :placeholder-font-size="18"
                        :show-remove-button="showRemoveBtn"
                        @new-image-drawn="newImageHandler"
                        @zoom="zoomHandler"
                        :prevent-white-space="true"
                        @init="initialize"
                        :initial-image="existingImage"
                        :key="existingImage"
                        accept=".jpeg,.png,.jpg,.bmp">
                    </croppa>
                </v-card-text>
                <status-bar :action="uploadAction"></status-bar>
                <v-card-actions>
                    <v-flex xs6>
                        <v-flex class="upload-photo__actions">
                            <i class="fas fa-user"></i>
                            <input type="range" class="slider" @input="onSliderChange" :min="sliderMin" :max="sliderMax" step=".001" v-model="sliderVal">
                            <i class="fas fa-user"></i>
                            <span class="rotate_btn">
                                <v-btn icon @click="croppa.rotate()"><i class="fas fa-undo"></i></v-btn>
                                <label>{{photoCropperConstant.ROTATE}}</label>
                                <v-btn icon @click="croppa.rotate(-1)"><i class="fas fa-redo"></i></v-btn>
                            </span>
                        </v-flex>
                    </v-flex>
                    <v-flex xs6 text-xs-right>
                        <v-flex ma-2>
                            <v-btn
                                flat
                                class="primary_btn transparent"
                                @click="croppa.chooseFile()"
                                data-qe-id="choose-another-photo_btn">
                                {{photoCropperConstant.CHOOSE_ANOTHER_PHOTO}}
                            </v-btn>
                            <v-btn
                                flat
                                class="primary_btn"
                                data-qe-id="save-photo_btn"
                                @click="photoUpload()">
                                {{photoCropperConstant.OK}}
                            </v-btn>
                        </v-flex>
                    </v-flex>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import FileUpload from 'vue-upload-component';
    import 'vue-croppa/dist/vue-croppa.css';
    import { mapState } from "vuex";
    import { photoCropperConstant } from "./photoCropper.constant";
    import StatusBar from "../StatusBar/StatusBar";

    export default {
        name: "PhotoCropper",
        data: () => ({
            photoCropperConstant,
            uploadPhotoDialog: false,
            myCroppa: null,
            croppa: {},
            sliderVal: 0,
            sliderMin: 0,
            sliderMax: 0,
            profileImageFailureFlags: {},
        }),
        components: {
            StatusBar,
            FileUpload,
        },
        props: {
            iconSize: {
                type: Number,
                default: 18,
            },
            existingImage: {
                type: String,
                required: true,
            },
            uploadActionName: {
                type: String,
                required: true,
            },
            cropperTitle: {
                type: String,
                default: photoCropperConstant.CROPPER_DEFAULT_TITLE,
            },
            cropperPlaceholder: {
                type: String,
                default: photoCropperConstant.PLACEHOLDER_DEFAULT,
            },
            postCropCallBack: {
                type: Function,
                required: true,
            },
            roundCropper: {
                type: Boolean,
                default: false,
            },
            showRemoveBtn: {
                type: Boolean,
                default: false,
            },
            uploadImmediately: {
                type: Boolean,
                default: true,
            },
        },
        methods: {
             dataURItoBlob(dataURI) {
                let fileName;
                // convert base64/URLEncoded data component to raw binary data held in a string
                const BASE64_MARKER = ';base64,';
                const mime = dataURI.split(BASE64_MARKER)[0].split(':')[1];
                if (this.croppa.chosenFile) {
                    fileName = this.croppa.chosenFile.name;
                }
                const bytes = atob(dataURI.split(BASE64_MARKER)[1]);
                const writer = new Uint8Array(new ArrayBuffer(bytes.length));

                for (let i = 0; i < bytes.length; i += 1) {
                    writer[i] = bytes.charCodeAt(i);
                }

                return new File([writer.buffer], fileName, { type: mime });
            },
            photoUpload() {
                let imageData = this.croppa.generateDataUrl();
                const imageFile = this.dataURItoBlob(imageData);
                imageData = imageData.substr(22);
                this.postCropCallBack({ imageData, imageFile });
                this.uploadPhotoDialog = false;
            },
            openPhotoDialog() {
                this.uploadPhotoDialog = true;
                this.sliderVal = 0;
            },
            newImageHandler() {
                this.sliderVal = this.croppa.scaleRatio;
                this.sliderMin = this.croppa.scaleRatio / 2;
                this.sliderMax = this.croppa.scaleRatio * 2;
            },
            onSliderChange(evt) {
                const increment = evt.target.value;
                this.croppa.scaleRatio = +increment;
            },
            zoomHandler() {
                this.sliderVal = this.croppa.scaleRatio;
            },
            initialize() {
                if (this.roundCropper) {
                    this.croppa.addClipPlugin((ctx, x, y, w, h) => {
                        ctx.beginPath();
                        ctx.arc((x + w) / 2, (y + h) / 2, w / 2, 0, 2 * Math.PI, true);
                        ctx.closePath();
                    });
                }
            },
        },
        computed: {
            ...mapState("actionState", ["actions"]),
            uploadAction() {
                return this.actions[this.uploadActionName];
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import "../../common/styles/_colors.scss";
    @import "../../common/styles/_responsive.scss";

    .photo-cropper {
        position: relative;

        .fa-camera {
            border: 2.6px solid $ypo-open-blue-gray;
            width: 30px;
            height: 30px;
            position: absolute;
            right: -10px;
            top: 70px;
            z-index: 1;
            border-radius: 50%;
            background: $ypo-cta;
            text-align: center;
            line-height: 24px;
            color: $white;
            cursor: pointer;
        }

    }

    .upload-photo {
        text-align: center;
        padding: 0;
        margin-right: 0px !important;
        background-color: $ypo-medium-gray;

        i {
            font-size: 20px;
            color: $ypo-medium-gray;
        }

        .rotate_btn {

            label {
                padding: 5px;
            }
        }

        &__actions {
            padding: 13px 0 10px 12px;
            margin-top: 1px;

            .fa-user {
                font-size: 12px;
                color: $ypo-blue;
                background: $white;
                width: 20px;
                border-radius: 50%;
                height: 20px;
                text-align: center;
                line-height: 20px;
            }

            span {
                margin-left: 30px;
                color: $ypo_blue;

                i {
                    font-size: 14px;
                    color: $ypo_blue;
                }

                button:focus {
                    outline: none;
                }
            }

            input[type=range] {
                width: 175px;
                margin: 0 5px;
                position: relative;
                top: -2px;

                &+i {
                    width: 26px;
                    height: 26px;
                    font-size: 16px;
                    line-height: 24px;
                }

                @include breakpoint(xs) {
                    width: 125px;
                }
            }

        }
    }
</style><style lang="scss">
    @import "../../common/styles/_colors.scss";

    .photo-cropper {

        &__dialog {
            .v-card__title {
                background-color: $ypo-tile-border;
                color: $ypo-blue;
                font-family: Raleway, sans-serif;
                font-size: 24px;
                font-weight: 500;
                line-height: 25px;
                position: relative;

                i {
                    color: $ypo-cta;
                    position: absolute;
                    right: 20px;
                    cursor: pointer;
                }
            }
        }
    }

    .croppa-container {
        background-color: $ypo-medium-gray;
        padding: 0px;
        width: 100%;
        height: 100%;

        &:hover {
            opacity: 1;
        }

        canvas {
            position: relative;
            border: 2px dashed #333;

            &:hover {
                opacity: 0.5;
            }
        }

        &.croppa--has-target {

            canvas {
                border: none;

                &:hover {
                    opacity: 1;
                }
            }
        }
    }

    .slider {
        -webkit-appearance: none;
        width: 100%;
        height: 3px;
        background: $ypo-gray;
        outline: none;
        opacity: 0.7;
        -webkit-transition: .2s;
        transition: opacity .2s;
        border-radius: 50px;
    }

    .slider:hover {
        opacity: 1;
    }

    .slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 20px;
        height: 20px;
        background: $ypo-blue;
        cursor: pointer;
        border-radius: 50px;
    }

    .slider::-moz-range-thumb {
        width: 20px;
        height: 20px;
        background: $ypo-blue;
        cursor: pointer;
    }
</style>
